import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';
import type { OrgChart } from './peopleApi';

// --- Types (mirror backend src/routes/projects.serializer.ts) --------------

export type ProjectStatus =
  | 'PLANNING'
  | 'ACTIVE'
  | 'ON_HOLD'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'ALMOST_DONE'
  | 'LEAD'
  | 'IN_HOUSE';
export type ProjectHealth = 'ON_TRACK' | 'AT_RISK' | 'OFF_TRACK';
export type ProjectRole = 'LEAD' | 'CONTRIBUTOR' | 'REVIEWER' | 'STAKEHOLDER';

export interface UserRef {
  id: string;
  name: string;
  email: string;
  avatarUrl: string | null;
}

export interface ProjectMember {
  id: string;
  role: ProjectRole;
  joinedAt: string;
  user: UserRef;
}

/** Currency a project's allocated budget is expressed in. */
export type ProjectBudgetCurrency = 'RON' | 'EUR';

export interface ProjectSummary {
  id: string;
  code: string | null;
  name: string;
  description: string | null;
  status: ProjectStatus;
  health: ProjectHealth;
  startDate: string | null;
  endDate: string | null;
  budget: number | null;
  /** The currency `budget` is in; defaults to RON for rows never edited. */
  budgetCurrency: ProjectBudgetCurrency;
  owner: UserRef | null;
  members: ProjectMember[];
  memberCount: number;
  // Multi-company: DB-only firm tag (NULL = default firm), edited via
  // PATCH /api/projects/:id/company — never written by the KB sync.
  companyId: string | null;
  company: { id: string; name: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface FinancialEntry {
  id: string;
  type: 'REVENUE' | 'COST';
  category: string;
  description: string | null;
  amount: number;
  currency: string;
  year: number;
  month: number;
  date: string;
}

// A Firmio-issued invoice tagged with the project (internal tag), with the
// firm that issued it — the project is billed by any firm.
export interface ProjectIssuedInvoice {
  id: string;
  fullNumber: string | null;
  status: 'DRAFT' | 'SENDING' | 'SENT' | 'ACCEPTED' | 'REJECTED' | 'FAILED' | 'CANCELLED';
  issueDate: string;
  dueDate: string;
  customerName: string;
  currency: string;
  netAmount: number;
  vatAmount: number;
  totalAmount: number;
  stornoOfId: string | null;
  company: { id: string; name: string } | null;
}

export interface ProjectInvoicedByCompany {
  company: { id: string; name: string };
  count: number;
  totalAmount: number;
}

export interface ProjectFinancials {
  budget: number | null;
  // Facturi emise for this project across firms. Optional for older payloads
  // (mobile test data, cached responses).
  issuedInvoices?: ProjectIssuedInvoice[];
  invoicedByCompany?: ProjectInvoicedByCompany[];
  totalInvoiced?: number;
  totalRevenue: number;
  totalCost: number;
  profit: number;
  profitMargin: number;
  entries: FinancialEntry[];
}

export interface ProjectResource {
  id: string;
  name: string;
  description: string | null;
  fileType: string | null;
  fileSize: number | null;
  s3Key: string;
  s3Url: string | null;
  createdAt: string;
}

export interface ProjectTaskRef {
  id: string;
  title: string;
  status: string;
  statusLabel?: string;
  statusColor?: string;
  statusCategory?: 'BACKLOG' | 'UNSTARTED' | 'STARTED' | 'COMPLETED' | 'CANCELLED';
  priority: string;
  assigneeId: string | null;
  dueDate: string | null;
}

// CLE-72: a team member derived from the project's allocations (Person <->
// Project), used by the Team tab instead of the legacy ProjectMember join.
export interface ProjectTeamMember {
  id: string;
  fullName: string;
  email: string;
  title: string | null;
  department: string | null;
  avatarUrl: string | null;
  managerId: string | null;
  allocationHours: number;
  isActive: boolean;
  joinedAt: string | null;
}

export interface ProjectTeam {
  members: ProjectTeamMember[];
  // Org-chart subtree limited to people on this project (reuses CLE-70 shape).
  orgChart: OrgChart;
}

// Services nomenclature: a service tag attached to the project (DB-only,
// edited via PATCH /api/projects/:id/services — not part of the KB markdown).
export interface ProjectServiceRef {
  id: string;
  name: string;
  color: string | null;
  category: string;
}

export interface ProjectDetail extends Omit<ProjectSummary, 'memberCount'> {
  team: ProjectTeam;
  // CLE-432: omitted by the backend for roles without projects.canViewFinancials.
  financials?: ProjectFinancials;
  resources: ProjectResource[];
  tasks: ProjectTaskRef[];
  services: ProjectServiceRef[];
  // Client association (CLE-79): DB-only link, editable from the Edit modal via
  // PATCH /api/kb/projects/:code (clientId).
  clientId: string | null;
  client: { id: string; name: string } | null;
}

export interface PageMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface Envelope<T> {
  success: true;
  data: T;
}

interface PagedEnvelope<T> extends Envelope<T> {
  meta: PageMeta;
}

// Attach the current access token from the auth store to every request.
function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface ProjectFilters {
  status?: ProjectStatus[];
  health?: ProjectHealth[];
  ownerId?: string;
  // Firm scope (multi-company): undefined = all firms.
  companyId?: string;
  search?: string;
  sort?: string;
  page?: number;
  limit?: number;
}

export interface ProjectsListResult {
  projects: ProjectSummary[];
  meta: PageMeta;
}

export async function listProjects(filters: ProjectFilters = {}): Promise<ProjectsListResult> {
  const params = new URLSearchParams();
  if (filters.status?.length) params.set('status', filters.status.join(','));
  if (filters.health?.length) params.set('health', filters.health.join(','));
  if (filters.ownerId) params.set('ownerId', filters.ownerId);
  if (filters.companyId) params.set('companyId', filters.companyId);
  if (filters.search) params.set('search', filters.search);
  if (filters.sort) params.set('sort', filters.sort);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));
  const qs = params.toString();
  const res = await apiFetch<PagedEnvelope<ProjectSummary[]>>(
    `/api/projects${qs ? `?${qs}` : ''}`,
    { headers: authHeaders() },
  );
  return { projects: res.data, meta: res.meta };
}

export async function getProject(id: string): Promise<ProjectDetail> {
  const res = await apiFetch<Envelope<ProjectDetail>>(`/api/projects/${id}`, {
    headers: authHeaders(),
  });
  return res.data;
}

// --- Resources DMS tree (CLE-48) -------------------------------------------

export type FileKind = 'pdf' | 'image' | 'doc' | 'file';

export interface InvoiceMeta {
  invoiceNumber: string;
  direction: 'INCOME' | 'OUTCOME';
  status: string;
  amount: number;
  taxAmount: number;
  amountTotal: number;
  currency: string;
  issueDate: string | null;
  dueDate: string | null;
  paidAt: string | null;
  vendor: string | null;
  client: string | null;
}

export interface TreeFile {
  type: 'file';
  id: string;
  name: string;
  kind: FileKind;
  fileType: string | null;
  fileSize: number | null;
  createdAt: string | null;
  downloadUrl: string | null;
  category: string;
  invoice?: InvoiceMeta;
}

export interface TreeFolder {
  type: 'folder';
  path: string;
  name: string;
  children: TreeNode[];
  fileCount: number;
  /** A subfolder someone created (deletable while empty), not a fixed bucket. */
  custom?: boolean;
}

export type TreeNode = TreeFolder | TreeFile;

export interface ResourcesTree {
  tree: TreeFolder;
  files: TreeFile[];
}

export async function getProjectResourcesTree(id: string): Promise<ResourcesTree> {
  const res = await apiFetch<Envelope<ResourcesTree>>(`/api/projects/${id}/resources/tree`, {
    headers: authHeaders(),
  });
  return res.data;
}

// --- Manual resource upload (Contract / Design / Altele) --------------------
//
// Mirrors the client resources flow: request a tokenised upload URL, PUT the
// bytes (uploadToSignedUrl from clientsApi), then confirm here. CONTRACT files
// enter the markdown-first contracts pipeline server-side; DESIGN/OTHER create
// the Resource row directly.

export const PROJECT_RESOURCE_CATEGORIES = ['CONTRACT', 'DESIGN', 'OTHER'] as const;
export type ProjectResourceCategory = (typeof PROJECT_RESOURCE_CATEGORIES)[number];

export interface ProjectUploadUrlResult {
  uploadUrl: string;
  key: string;
  expiresIn?: number;
}

export async function requestProjectResourceUploadUrl(
  projectId: string,
  input: {
    fileName: string;
    category: ProjectResourceCategory;
    contentType: string;
    fileSize?: number;
    /** Subfolder tree path ("design/Mockup-uri"); omitted = the bucket itself. */
    folderPath?: string;
  },
): Promise<ProjectUploadUrlResult> {
  const res = await apiFetch<Envelope<ProjectUploadUrlResult>>(
    `/api/projects/${projectId}/resources/upload-url`,
    { method: 'POST', headers: authHeaders(), body: JSON.stringify(input) },
  );
  return res.data;
}

export async function createProjectResource(
  projectId: string,
  input: {
    name: string;
    category: ProjectResourceCategory;
    s3Key: string;
    description?: string;
    fileType?: string;
    fileSize?: number;
    folderPath?: string;
  },
): Promise<{ id: string | null; name: string; category: string; downloadUrl: string | null }> {
  const res = await apiFetch<
    Envelope<{ id: string | null; name: string; category: string; downloadUrl: string | null }>
  >(`/api/projects/${projectId}/resources`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

// Delete a resource. Contract-backed rows also lose their KB markdown source
// server-side (otherwise the sync would resurrect them). Invoices are NOT
// deletable here — the tree derives them from the Invoice table.
export async function deleteProjectResource(projectId: string, resourceId: string): Promise<void> {
  await apiFetch<Envelope<{ id: string; deleted: boolean }>>(
    `/api/projects/${projectId}/resources/${resourceId}`,
    { method: 'DELETE', headers: authHeaders() },
  );
}

// Subfolders inside Contracte / Design / Alte resurse. `path` is the full tree
// path ("design/Mockup-uri/v2"); deleting only works while the folder is empty.
export async function createProjectResourceFolder(
  projectId: string,
  path: string,
): Promise<{ path: string }> {
  const res = await apiFetch<Envelope<{ path: string }>>(
    `/api/projects/${projectId}/resource-folders`,
    { method: 'POST', headers: authHeaders(), body: JSON.stringify({ path }) },
  );
  return res.data;
}

export async function deleteProjectResourceFolder(projectId: string, path: string): Promise<void> {
  await apiFetch<Envelope<{ path: string; deleted: boolean }>>(
    `/api/projects/${projectId}/resource-folders?path=${encodeURIComponent(path)}`,
    { method: 'DELETE', headers: authHeaders() },
  );
}

// Projects are markdown-first (CLE-41): create/update/delete and member changes
// happen through the AI assistant (KB markdown -> sync). Only read functions
// remain in this client — with one exception below: service links are DB-only
// metadata edited via a dedicated PATCH (same precedent as person.department).

// Multi-company: set/clear the DB-only firm tag (NULL = default firm). Same
// precedent as services above — the KB sync never touches `companyId`.
export async function updateProjectCompany(
  id: string,
  companyId: string | null,
): Promise<{ id: string; companyId: string | null; company: { id: string; name: string } | null }> {
  const res = await apiFetch<
    Envelope<{ id: string; companyId: string | null; company: { id: string; name: string } | null }>
  >(`/api/projects/${id}/company`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify({ companyId }),
  });
  return res.data;
}

export async function updateProjectServices(
  id: string,
  serviceIds: string[],
): Promise<ProjectServiceRef[]> {
  const res = await apiFetch<Envelope<{ services: ProjectServiceRef[] }>>(
    `/api/projects/${id}/services`,
    {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify({ serviceIds }),
    },
  );
  return res.data.services;
}
